// pages/OrganizerPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { getEventsByOrganizer, searchEventsByOrganizerName } from '../services/strapiService';
import styled from 'styled-components';

const OrganizerPageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  min-height: 100vh;
`;

const Header = styled.div`
  margin-bottom: 30px;
  text-align: center;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 18px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  margin: 20px 0;
`;

const NoEventsContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const EventCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
  }
`;

const EventImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 500;
`;

const EventContent = styled.div`
  padding: 20px;
`;

const EventTitle = styled.h3`
  color: #333;
  margin: 0 0 10px 0;
  font-size: 18px;
  line-height: 1.4;
`;

const EventInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 15px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  color: #666;
  font-size: 14px;
  
  strong {
    color: #333;
    margin-right: 8px;
    min-width: 60px;
  }
`;

const EventDescription = styled.p`
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin: 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ViewEventButton = styled(Link)`
  display: inline-block;
  background: #007bff;
  color: white;
  text-decoration: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #0056b3;
    text-decoration: none;
    color: white;
  }
`;

const BackButton = styled(Link)`
  display: inline-block;
  background: #6c757d;
  color: white;
  text-decoration: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #545b62;
    text-decoration: none;
    color: white;
  }
`;

const OrganizerPage = () => {
  const { organizerId } = useParams();
  const [searchParams] = useSearchParams();
  const organizerName = searchParams.get('name');
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organizerInfo, setOrganizerInfo] = useState(null);

  useEffect(() => {
    const fetchOrganizerEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let response;
        
        if (organizerId) {
          // Cerca eventi per ID organizzatore
          console.log('Recupero eventi per organizzatore ID:', organizerId);
          response = await getEventsByOrganizer(organizerId);
        } else if (organizerName) {
          // Cerca eventi per nome organizzatore alternativo
          console.log('Recupero eventi per organizzatore nome:', organizerName);
          response = await searchEventsByOrganizerName(organizerName);
        } else {
          throw new Error('Nessun parametro organizzatore fornito');
        }
        
        if (response) {
          setEvents(response.events || []);
          setOrganizerInfo(response.organizer || { name: organizerName });
        }
      } catch (err) {
        console.error('Errore nel recupero eventi organizzatore:', err);
        setError(err.message || 'Errore nel caricamento degli eventi');
      } finally {
        setLoading(false);
      }
    };

    if (organizerId || organizerName) {
      fetchOrganizerEvents();
    } else {
      setLoading(false);
      setError('Parametri organizzatore mancanti');
    }
  }, [organizerId, organizerName]);

  // Funzione per formattare la data
  const formatDate = (dateString) => {
    if (!dateString) return 'Data non disponibile';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Data non valida';
    }
  };

  // Funzione per ottenere il nome dell'organizzatore
  const getOrganizerDisplayName = () => {
    if (organizerInfo) {
      return organizerInfo.username || organizerInfo.name || 'Organizzatore';
    }
    return organizerName || 'Organizzatore';
  };

  if (loading) {
    return (
      <OrganizerPageContainer>
        <BackButton to="/">← Torna alla mappa</BackButton>
        <LoadingContainer>
          <div>Caricamento eventi dell'organizzatore...</div>
        </LoadingContainer>
      </OrganizerPageContainer>
    );
  }

  if (error) {
    return (
      <OrganizerPageContainer>
        <BackButton to="/">← Torna alla mappa</BackButton>
        <ErrorContainer>
          <h3>Errore nel caricamento</h3>
          <p>{error}</p>
        </ErrorContainer>
      </OrganizerPageContainer>
    );
  }

  return (
    <OrganizerPageContainer>
      <BackButton to="/">← Torna alla mappa</BackButton>
      
      <Header>
        <Title>Eventi di {getOrganizerDisplayName()}</Title>
        <Subtitle>
          {events.length === 0 
            ? 'Nessun evento trovato' 
            : `${events.length} evento${events.length !== 1 ? 'i' : ''} trovato${events.length !== 1 ? 'i' : ''}`
          }
        </Subtitle>
      </Header>

      {events.length === 0 ? (
        <NoEventsContainer>
          <h3>Nessun evento trovato</h3>
          <p>Questo organizzatore non ha ancora pubblicato eventi.</p>
        </NoEventsContainer>
      ) : (
        <EventsGrid>
          {events.map(event => (
            <EventCard key={event.id}>
              <EventImage>
                {event.image && event.image.length > 0 ? (
                  <img 
                    src={event.image[0].url} 
                    alt={event.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <span>📅 {event.title}</span>
                )}
              </EventImage>
              
              <EventContent>
                <EventTitle>{event.title}</EventTitle>
                
                <EventInfo>
                  <InfoItem>
                    <strong>📍</strong>
                    <span>{event.location}</span>
                  </InfoItem>
                  
                  <InfoItem>
                    <strong>📅</strong>
                    <span>{formatDate(event.startDate)}</span>
                  </InfoItem>
                  
                  {event.ticketPrice !== undefined && event.ticketPrice !== null && (
                    <InfoItem>
                      <strong>💰</strong>
                      <span>{event.ticketPrice === 0 ? 'Gratuito' : `€ ${event.ticketPrice}`}</span>
                    </InfoItem>
                  )}
                  
                  {event.isExpired && (
                    <InfoItem>
                      <strong>⚠️</strong>
                      <span style={{ color: '#e74c3c' }}>Evento terminato</span>
                    </InfoItem>
                  )}
                </EventInfo>

                {event.description && (
                  <EventDescription>
                    {event.description}
                  </EventDescription>
                )}

                <ViewEventButton to={`/event/${event.id}`}>
                  Visualizza Dettagli
                </ViewEventButton>
              </EventContent>
            </EventCard>
          ))}
        </EventsGrid>
      )}
    </OrganizerPageContainer>
  );
};

export default OrganizerPage;
